import React,{useEffect,useRef,useState} from 'react';
import {FiGitMerge,FiX,FiLink,FiCheckCircle,FiAlertCircle} from 'react-icons/fi';
import './ProjectMergeDialog.css';

const label=row=>row?.data?.projectName||row?.data?.title||'Untitled project';
export default function ProjectMergeDialog({source,projects,onPreview,onMerge,onClose}){
 const [destination,setDestination]=useState('');
 const [appendAssembly,setAppendAssembly]=useState(true);
 const [preview,setPreview]=useState(null);
 const [loading,setLoading]=useState(false);
 const [working,setWorking]=useState(false);
 const [error,setError]=useState('');
 const previewFn=useRef(onPreview);
 previewFn.current=onPreview;
 const targets=projects.filter(row=>row.id!==source.id);
 useEffect(()=>{
  if(!targets.some(row=>row.id===destination))
   setDestination(targets[0]?.id||'');
 },[source.id,projects.length,destination]);
 useEffect(()=>{
  if(!destination){setPreview(null);return;}
  let live=true;
  setLoading(true);setPreview(null);setError('');
  previewFn.current(source.id,destination,appendAssembly).then(data=>{
   if(live){setPreview(data);setLoading(false);}
  }).catch(err=>{
   if(live){setError(err.message||'Could not preview the merge.');setLoading(false);}
  });
  return()=>{live=false;};
 },[source.id,destination,appendAssembly]);

 const canMerge=Boolean(!loading&&preview&&!working&&
  (preview.added_interviews||preview.append_passages&&appendAssembly));
 const submit=async()=>{
  if(!canMerge)return;
  setWorking(true);setError('');
  try{
   const result=await onMerge(source.id,destination,appendAssembly);
   if(!result)throw new Error('The merge did not complete. Your projects have not been removed.');
   onClose();
  }catch(e){setError(e.message||'Could not merge these projects.');}
  finally{setWorking(false);}
 };
 return <div className="modal-backdrop project-merge-overlay" role="presentation">
  <section className="modal project-merge-dialog" role="dialog" aria-modal="true"
   aria-labelledby="project-merge-title" aria-describedby="project-merge-help">
   <button className="project-merge-close" type="button" disabled={working}
    onClick={onClose} aria-label="Close merge dialog"><FiX/></button>
   <div className="project-merge-title"><FiGitMerge aria-hidden="true"/><h2 id="project-merge-title">Merge projects</h2></div>
   <p id="project-merge-help" className="project-merge-help">Bring interviews and optionally Assembly passages from one project into another. <strong>The source project is kept unchanged.</strong></p>
   <div className="project-merge-fields">
    <label>From project
     <strong className="project-merge-source">{label(source)}</strong>
    </label>
    <label htmlFor="project-merge-target">Into project
     <select id="project-merge-target" value={destination} disabled={working||!targets.length}
      onChange={event=>setDestination(event.target.value)}>
      {targets.map(row=><option value={row.id} key={row.id}>{label(row)}</option>)}
     </select>
    </label>
   </div>
   <label className="project-merge-assembly">
    <input type="checkbox" checked={appendAssembly} disabled={working}
     onChange={event=>setAppendAssembly(event.target.checked)}/>
    <span><strong>Also append the source Assembly</strong><small>Add its passages and section headings after the destination's existing edit. Existing content is never replaced.</small></span>
   </label>
   {loading&&<p className="project-merge-loading" role="status">Checking shared interviews and story material…</p>}
   {preview&&!loading&&<div className="project-merge-preview" role="status">
    <div className="project-merge-stats">
     <div><strong>{preview.added_interviews}</strong><span>Interview reference{preview.added_interviews===1?'':'s'} to add</span></div>
     <div><strong>{preview.already_in_destination}</strong><span>Already linked</span></div>
     <div><strong>{appendAssembly?preview.append_passages:0}</strong><span>Passages to append</span></div>
    </div>
    <p className="project-merge-storage"><FiCheckCircle aria-hidden="true"/> <strong>0 MB additional uploaded storage</strong> · Existing Media Library videos are reused, not uploaded again.</p>
    {!!preview.files?.length&&<details className="project-merge-files">
     <summary>Review interview files ({preview.files.length})</summary>
     <ul>{preview.files.map((file,i)=><li key={file.source_id||i}>
      <FiLink aria-hidden="true"/><span title={file.name}>{file.name}</span>
      <small>{file.reused?'Already in destination':'Link to destination'}</small>
     </li>)}</ul>
    </details>}
    <p className="project-merge-safety">The source project, original files, its client feedback, named versions and AI checkpoints remain untouched. Imported passages receive new IDs; destination history and existing Assembly stay intact.</p>
   </div>}
   {!targets.length&&<p className="project-merge-warning"><FiAlertCircle/> Create another project before merging.</p>}
   {preview&&!canMerge&&!working&&!loading&&<p className="project-merge-warning">Nothing new to merge with these settings. Choose a different destination or include the source Assembly.</p>}
   {error&&<p className="project-merge-error" role="alert">{error}</p>}
   <footer className="project-merge-actions">
    <button type="button" className="project-merge-cancel" disabled={working} onClick={onClose}>Cancel</button>
    <button type="button" className="project-merge-confirm" disabled={!canMerge} onClick={submit}>
     <FiGitMerge aria-hidden="true"/> {working?'Merging…':'Merge into project'}
    </button>
   </footer>
  </section>
 </div>;
}
